import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { TextField, TextFieldProps } from "@mui/material";

type Unit = "d" | "h";

const _UNIT_TO_HOURS: Record<Unit, number> = {
    d: 24,
    h: 1,
} as const;

const _extractUnitValueFromSum = (sumValue: number, unit: "d" | "h"): number => {
    // console.log(sumValue, unit);
    switch (unit) {
        case "d":
            // console.log(Math.floor(sumValue / _UNIT_TO_HOURS.d));
            return Math.floor(sumValue / _UNIT_TO_HOURS.d);
        case "h":
            // console.log(Math.floor(sumValue / _UNIT_TO_HOURS.d));
            return sumValue % _UNIT_TO_HOURS.d;
    }
};

const _unitValuesToHours = (days: number, hours: number): number => {
    return days * _UNIT_TO_HOURS.d + hours;
};

export const formatDurationHours = (inputValue: number): string => {
    const days = _extractUnitValueFromSum(inputValue, "d");
    const hours = _extractUnitValueFromSum(inputValue, "h");

    // probably it is the best if we only use days and hours here
    const parts = [days ? `${days}d` : "", hours ? `${hours}h` : ""].filter(Boolean);

    if (parts.length === 0) {
        return "0h";
    }

    return parts.join(" ");
};

/** parses '1d 2h' */
export const parseDurationTextInput = (inputValue: string): number => {
    const units: Unit[] = ["d", "h"];

    const parts = inputValue.split(" ");
    if (parts.length === 0) {
        return -1;
    }

    const values: Record<Unit, number> = { d: 0, h: 0 };

    // for checking duplicates
    const unitsInText: Unit[] = [];

    for (const part of parts) {
        const unit = part[part.length - 1] as Unit;
        const value = parseInt(part.slice(0, part.length - 1), 10);

        if (units.includes(unit) && !isNaN(value)) {
            if (unitsInText.includes(unit)) {
                // returning -1 for invalid format (validator handles it)
                return -1;
            }

            unitsInText.push(unit);
            values[unit] = value;
        } else {
            // returning -1 for invalid format (validator handles it)
            return -1;
        }
    }

    return _unitValuesToHours(values.d, values.h);
};

export interface DurationFieldProps
    extends Omit<TextFieldProps, "variant" | "label" | "fullWidth" | "type" | "onChange" | "value"> {
    label?: string;
    value: number;
    onChange: (value: number) => void;
}

const DurationField = React.forwardRef<HTMLDivElement, DurationFieldProps>(
    ({ label, value, error, onChange, ...props }, ref) => {
        const [displayValue, setDisplayValue] = useState(formatDurationHours(value));
        const [valueError, setValueError] = useState(false);

        // Update display value when field.value changes externally
        useEffect(() => {
            if (value !== -1) {
                setDisplayValue(formatDurationHours(value));
                setValueError(false);
            }
        }, [value]);

        return (
            <TextField
                ref={ref}
                variant="outlined"
                label={label ? <FormattedMessage id={label} /> : null}
                type="text"
                value={displayValue}
                onChange={(e) => {
                    const newValue = e.target.value;
                    setDisplayValue(newValue);
                    const parsed = parseDurationTextInput(newValue);
                    setValueError(parsed <= 0);
                    onChange(parsed);
                }}
                required
                fullWidth
                error={error || valueError}
                {...props}
            />
        );
    },
);

export default DurationField;
